import { Card } from '@citizen-walk/shared-ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * 管理者ガイドページ (Sprint BG-DataSettings 17、A-S28 拡張、旧 `old/apps/admin/src/pages/GuidePage.tsx` (495 行) を L3 踏襲)。
 *
 * 旧 v1 の 12 セクション + 用語集 + トラブルシューティングを v2 admin の主要 8 領域に再構成:
 *   1. 地区管理 (CRUD / token 再発行 / archive)
 *   2. データ設定 (userFields / postFields editor + Sprint 3 の新ページ)
 *   3. 投稿モデレーション
 *   4. ユーザー管理 / 権限 (Custom Claims)
 *   5. マスター管理 (M1〜M9)
 *   6. エクスポート / インポート
 *   7. AI 解析設定
 *   8. アンケート / 検証
 *
 * 旧との差分:
 *   - 旧 12 → v2 8 セクション (M5 displayRules / data-settings 新 page 等 v2 で追加された領域反映)
 *   - 旧 BrandBadge / NewBadge / Step 番号付きカード → v2 では Card + Tailwind list で簡素化
 *   - 旧の useAuth による master 判定は v2 では割愛 (v2 admin guide は両 role 共通 view + master-only 注記)
 *   - 用語集 + トラブルシューティングは末尾に展開
 */

/** AW10 (docs/108): セクション内に表 (ロール権限表・地区モード解説表) を埋め込むための構造。 */
interface GuideTable {
  caption?: string;
  headers: string[];
  rows: string[][];
}

interface GuideSection {
  id: string;
  title: string;
  body: string[];
  bullets?: string[];
  links?: { label: string; to: string; external?: boolean }[];
  masterOnly?: boolean;
  hint?: string;
  /** AW10 High: ログイン手順のロール表・地区モード解説表 (old2 GuidePage §1 + ModeTable 踏襲)。 */
  tables?: GuideTable[];
}

const SECTIONS: GuideSection[] = [
  {
    id: 'getting-started',
    title: 'はじめに（画面の見方）',
    body: [
      '左のメニューは役割ごとに分かれています。「ホーム」（最初に表示される画面）、「担当地区」（あなたが管理する地区を選ぶ）、「全地区」（マスター管理者のみ、すべての地区の一覧と新規作成）、「テンプレ・マスタ」（マスター管理者のみ、AI 解析やアンケートなどのひな形）、「権限・監査」（マスター管理者のみ、管理者の招待と操作ログ）、「使い方ガイド」（このページ）です。',
      'ほとんどの設定は地区ごとです。まず左メニューで対象の地区を選んでください。地区を選ぶと、その地区専用のメニュー（投稿モデレーション・お知らせ・セッション・入力項目・エクスポートなど）が左に開きます。',
      '日々の使い方の流れ: 地区を選ぶ → 投稿モデレーション / お知らせ / セッション → エクスポート、の順で進めるとスムーズです。',
    ],
    links: [{ label: '担当する地区を選ぶ', to: '/districts' }],
  },
  {
    id: 'login',
    title: '1. ログインと権限管理',
    body: [
      '配布されたログイン URL を開き、下記いずれかの方法でサインインします。',
      'Google でログイン: 招待されている Google アカウントで簡単ログイン。メールアドレス + パスワード: 招待時に発行されたメールリンクから初回パスワードを設定 (本番)。Emulator では master@example.com / masterpass。',
      'パスワードを忘れた場合は、サインイン画面でメールアドレスを入力して「パスワードをお忘れの方」を押すと、再設定メールが届きます。',
    ],
    bullets: [
      '管理者の招待 (master only): サイドバー「ユーザー管理」→ 対象 user に role (master / district) + 担当地区 (districts) を付与',
      'Google アカウント所有者は、招待 (role 付与) 後すぐ「Google でログイン」から入れます',
      'メール招待の場合は、初回ログイン時にパスワード再設定メールから初期パスワードを設定',
    ],
    tables: [
      {
        caption: 'ロールの違い',
        headers: ['ロール', '権限'],
        rows: [
          ['マスター管理者', '全地区の管理、管理者の招待、AI 設定、データインポート、操作ログ閲覧'],
          [
            '地区管理者',
            '担当地区のみの管理（地区設定、セッション、モデレーション、エクスポート、アンケート、お知らせ）',
          ],
        ],
      },
    ],
    links: [{ label: 'ユーザー管理を開く', to: '/users' }],
    hint: '権限を変更したら、対象ユーザーは ログアウト → 再ログイン が必要です（Custom Claims 反映のため）。',
  },
  {
    id: 'district',
    title: '2. 地区管理',
    body: [
      '地区 (district) は CitizenWalk のすべての参加・投稿・分析の最小単位です。',
      '新しい地区は「地区管理」一覧から作成し、name / mode / urlToken (32-byte hex) を設定します。参加者は `https://<app>/m/<urlToken>` 経由でアクセス。',
      'urlToken を漏らした / 配布範囲を変えたい場合は「token 再発行」で即時無効化可能 (既存参加 URL は使えなくなります)。',
      'イベントで会場の PC に打ち込んでもらう場合は、地区概要の「短縮アドレス」から短いアドレス (例 `https://<app>/s/hakone`) を期間つき (最長 360 日) で発行できます。期間が過ぎても削除しなければ、期間を入れ直すだけで同じアドレスが復活します (掲示物の刷り直し不要)。',
    ],
    bullets: [
      '地区モード: bousai (防災) / ecw (ECW ウェルビーイング) / walker (ウォーカブル) / kids-bousai (子供向け防災)',
      '投稿エリア: postAreaCenter (緯度経度) + postAreaRadiusM (m) で円形範囲。範囲外投稿は警告',
      'colorRule: 投稿ピン色を quadrant (4 象限) or value (式 + 色) で定義 (M5 displayRules)',
      'archive: 作業終了地区を archived=true で隠す (データは保持)',
      'pmtiles: 「オフライン地図」セクションから生成 → 参加者がオフラインでも地図表示可。生成後は「🔍 検査ツールで実描画を確認」で MapLibre 実描画 / theme 切替 / schema mismatch を検証可',
    ],
    tables: [
      {
        caption: '地区モード一覧',
        headers: ['モード', '用途', '投稿タイプ'],
        rows: [
          ['防災モード (bousai)', '防災まちあるき', '危険箇所 / 防災資源 / 気づき'],
          [
            'ECW モード (ecw)',
            'ウェルビーイング調査',
            'Happy/Unhappy × Healthy/Unhealthy + 感情・対象分類',
          ],
          [
            'ウォーカブルモード (walker)',
            '歩きやすさ調査',
            'いいね！/もったいない × 対象物 × GQL 観点 × 国交省指標',
          ],
          [
            '子供向け防災モード (kids-bousai)',
            '子供向け防災まちあるき',
            'ホッと / ハッと / ヒント',
          ],
        ],
      },
    ],
    links: [{ label: '地区管理を開く', to: '/districts' }],
    hint: 'URL トークンを再発行すると古い URL/QR は即無効になります。配布済みの場合は注意。なお短縮アドレスは再発行後も新しい URL につながり続けるため、止めたいときは短縮アドレス側も「今すぐ停止」してください。',
  },
  {
    id: 'data-settings',
    title: '3. 入力項目（データ設定）',
    body: [
      'サイドバー「入力項目」または地区詳細から、投稿時の入力項目 (postFields) とユーザー登録時の項目 (userFields) を地区ごとに編集できます (Sprint BG-DataSettings 3 で完全実装、A-S9a)。',
      'mode (必須/任意/非表示) + options (select 等) + visible (投稿詳細での表示) + displayOrder (並び順) を細かく制御。10 種の field type (text / longText / number / rating-scale / select / multiselect / date / boolean / location / photo) に対応。',
    ],
    bullets: [
      'mode = required / optional / hidden の 3 段階で入力強制度を制御',
      'rating-scale 型: min / max / excludeZero / scaleLabels で 5 段階等の評価軸を定義',
      'visible=false: 投稿詳細で項目を隠す (データは保持、後で再公開可)',
      'displayOrder: 投稿詳細の項目並び順 (昇順)',
    ],
    hint: '項目を削除する代わりに visible=false を使えば、過去データを保ったまま隠せます。',
    links: [{ label: '対象の地区を開く', to: '/districts' }],
  },
  {
    id: 'moderation',
    title: '4. 投稿モデレーション',
    body: [
      '地区詳細から「投稿モデレーション」に進み、visibility を 3 段階 (`public` / `hidden_for_user` / `hidden_all`) で切替できます。',
      'master / 担当地区の district admin のみ操作可能。変更は operationLogs に audit 記録されます。',
    ],
    bullets: [
      'public: 全員に表示',
      'hidden_for_user: 投稿者本人と管理者のみ閲覧可',
      'hidden_all: 管理者のみ閲覧可 (データは残る)',
      '完全削除: 別アクション、不可逆。Firestore + 写真ストレージから削除',
      '一括操作: 複数選択 → モデレーション状態を一括変更',
      'AI 解析ステータスでフィルタ可 (失敗のみ / 未実施のみ / 完了 / スキップ)',
    ],
    hint: '同一データが複数 import された場合は visibility=hidden_all + moderationReason に理由を記録すると後で原状復帰しやすくなります。',
    links: [{ label: '対象の地区を開く', to: '/districts' }],
  },
  {
    id: 'users',
    title: '5. ユーザー管理と操作ログ',
    body: [
      '「ユーザー管理」から既存 Firebase Auth user に master / district / none の role を付与できます (master only)。',
      '`role=district` 時は `districts` 配列を指定し、該当地区のみアクセス可能に。自己 downgrade は禁止 (master 自己ロック防止)。',
      '操作ログでは、お知らせ・セッションなどの操作履歴を確認できます。',
    ],
    bullets: [
      'master: 全地区の管理 + 招待 + AI 設定 + import + operationLogs',
      'district: 担当地区のみ (設定 / セッション / モデレーション / export / アンケート / お知らせ)',
      'Custom Claims `accessibleDistricts`: 20 地区上限 (古いアクセスから FIFO で削除)',
      '権限変更後はログアウト → 再ログインが必要 (Custom Claims 反映のため)',
    ],
    links: [
      { label: 'ユーザー管理を開く', to: '/users' },
      { label: '操作ログを開く', to: '/operation-logs' },
    ],
    masterOnly: true,
  },
  {
    id: 'masters',
    title: '6. テンプレ・マスタ管理',
    body: [
      'AI 解析・出力フォーマット・カテゴリ・対話プロンプト・表示設定・カード印刷・アンケート・検証テンプレートの 9 種を CRUD 可能 (master only)。',
      '変更は `/<master>/{id}/history` に自動的に diff として記録され、誰がいつ何を変えたか追跡できます。',
    ],
    bullets: [
      'AI 解析 統合エディタ: 解析設定・出力フォーマット・カテゴリを1画面で編集',
      'モードプロファイル: 新しい地区を作るときの初期の入力項目とおすすめ設定',
      'AI カテゴリ (aiCategoryMasters): 分類カテゴリ (防災 / ECW / GQL / 国交省指標 等)',
      'AI 出力フォーマット (aiOutputFormats): AI 出力 JSON Schema の宣言的定義 (rootKey / classificationAxes / assessment)',
      'AI 対話設定 (aiDialogueSettings): 分析タブの 4-step 対話プリセット (観察 → 診断 → 処方箋 → 期待効果)',
      '分析表示設定 (analysisDisplayConfigs): マトリクス軸 / ヒートマップ / ネットワークグラフの bucket / weight',
      'スコア軸定義 (scoreDimensionMasters): スコア集約軸 (postDetailScores 用)',
      'カード印刷テンプレ (cardPrintTemplates): カード印刷レイアウト',
      'アンケートテンプレ (surveyPresets): アンケートのテンプレート',
      '検証テンプレ (verificationTemplatePresets): AI 検証用テンプレート',
    ],
    links: [
      { label: 'AI 解析 統合エディタを開く', to: '/masters/ai-analysis-profile' },
      { label: 'モードプロファイルを開く', to: '/masters/mode-profiles' },
    ],
    masterOnly: true,
  },
  {
    id: 'data-io',
    title: '7. エクスポート / インポート',
    body: [
      'エクスポートは地区詳細「エクスポート」から CSV / GeoJSON / 写真 ZIP / Drive バックアップを提供 (個人情報除外 toggle あり)。',
      'インポートはサイドバー「インポート」から CSV ファイル / Google Sheets URL を投入 (master only、A-S26 minimum)。dryRun で件数確認 → 本投入。',
    ],
    bullets: [
      'CSV エクスポート: UTF-8 with BOM (Excel で直接開ける)',
      'GeoJSON: QGIS 等の GIS ソフトで利用可',
      '写真 ZIP: 投稿写真を一括ダウンロード',
      'CSV インポート: header → field 自動推定 + preview 50 行 + error list',
      'Google Sheets / Drive: サービスアカウントメールに「閲覧者」権限を共有 (インポート画面のコピーボタンで取得)',
      'カード印刷: 地区詳細「カード印刷」で A4 シート向けレイアウト (写真 + QR + メタ情報)',
    ],
    links: [{ label: '対象の地区を開く', to: '/districts' }],
  },
  {
    id: 'ai',
    title: '8. AI 解析設定',
    body: [
      '投稿は写真ありの場合に自動的に AI 解析されます (Gemini)。AI 解析設定は地区別の `aiAnalysis.settingsId` で M1 master を切替できます。',
      '解析失敗 / 設定変更後の再解析は地区詳細の「AI 強制再解析」パネルから実行 (master only、1 回/分、最大 50 件)。',
    ],
    bullets: [
      'aiStatus: pending / processing / done / failed / skipped / null',
      '「未解析・失敗を抽出して一括解析」ボタン: 写真あり && failed/pending/null のみを順次再解析',
      '「一括再解析」ボタン: 完了済みも含めて全件やり直し (AI 設定変更後に使用)',
      'モード別解析結果: ECW (感情 + 対象分類) / 防災 (専門家コメント + 分類) / ウォーカブル (いいね/もったいないスコア + 分類)',
      'カテゴリマスタは categoryMasterIds[] で複数指定可',
    ],
    links: [{ label: '対象の地区を開く', to: '/districts' }],
    masterOnly: true,
  },
  {
    id: 'surveys',
    title: '9. アンケート / 検証 / お知らせ / セッション',
    body: [
      'サイドバー「アンケート」から参加者向けアンケートを作成 (質問項目 = テキスト / 選択 / レーティング)。回答は CSV/JSON エクスポート可。',
      '「AI 解析の検証」では参加者に AI 分類の妥当性を検証してもらうフローを構築 (verification-admin、M9 テンプレート駆動)。',
      '「お知らせ」は banner (画面上部に常時) / modal (起動時ダイアログ) の 2 形式 + level (info/warning/critical) + 期間 + 対象地区を指定。',
      '「セッション」は地区内の運用単位 (例: 1 日のまち歩きイベント)。開始/終了日時 + 参加者専用 URL (urlToken + sessionId) を発行。',
    ],
    bullets: [
      'ECW モード地区: 気づきカード / 処方箋カード (insights-moderation) の表示/非表示/削除も可能',
      'お知らせ レベル: info (青) / warning (黄) / critical (赤)',
      'セッション URL: `/m/<urlToken>/<sessionId>` 形式、OnboardingPage で対象セッションが自動チェック',
    ],
  },
  {
    id: 'map-extras',
    title: '10. 地図データと手書きメモ',
    body: [
      'GIS データ管理では、地図に重ねて表示する GIS データ（境界線や施設の位置などの地図ファイル）を取り込み、内容に応じて色分けできます。地区を選び、左メニューの「GISデータ」から設定します。',
      '手書きメモのモデレーションでは、参加者が地図に直接描いた手書きメモを、公開するか非表示にするかを管理できます。地区を選び、左メニューの「地図・表示の設定」から確認でき、不適切なメモは非表示にしてください。',
    ],
    bullets: [
      'GIS データ: GeoPackage / KML / GeoJSON などのファイルを取り込み、地図に重ねて表示',
      'GIS データ: 属性の値ごとに色分け（凡例も自動表示）',
      '手書きメモ: 参加者が地図に描いたメモを公開 / 非表示で切り替え',
    ],
    links: [{ label: '対象の地区を開く', to: '/districts' }],
  },
];

const TROUBLESHOOT: { q: string; a: string }[] = [
  {
    q: '参加者が URL にアクセスできない',
    a: 'URL トークンが正しいか確認してください。トークンを再発行した場合、古い URL は無効です。新しい URL/QR を配布してください。',
  },
  {
    q: '投稿が地図に表示されない',
    a: '投稿の visibility を確認してください。`hidden_for_user` / `hidden_all` の投稿は他のユーザーには見えません。',
  },
  {
    q: 'AI 解析が「失敗」になる',
    a: '投稿モデレーション画面の「AI解析」フィルタで「失敗のみ」を選び、「未解析・失敗を抽出して一括解析」ボタンで再試行できます。写真がない投稿では AI 解析は実行されません。',
  },
  {
    q: 'ログインできない (権限がありません)',
    a: 'master 管理者に連絡して、Custom Claims (role) を設定してもらってください。設定後はログアウト → 再ログインが必要です。',
  },
  {
    q: 'スプレッドシート / Drive からインポートできない',
    a: 'サービスアカウントメールに「閲覧者」権限を共有してください。インポートページのコピーボタンからメールアドレスを取得できます。',
  },
  {
    q: 'colorRule で色が出ない',
    a: 'value タイプは field key の入力ミス、quadrant タイプは xKey/yKey の値が customFields に存在するか確認してください。地区詳細の DataSettings で実フィールドを確認できます。',
  },
  {
    q: 'QR コードがぼやけて読み取れない',
    a: '既定設定 (360px、誤差訂正レベル M) で多くの環境で読み取れます。印刷時にぼやける場合は、ダウンロードした PNG をそのまま使用し、再リサイズしないでください。',
  },
  {
    q: '投稿詳細にスコアが表示されない',
    a: '(1) 該当地区の AnalysisDisplayConfig に postDetailScores が定義されているか、(2) DataSettings 側で対応フィールドの visible が true になっているか の 2 点を確認してください。',
  },
  {
    q: 'pmtiles 生成が失敗する',
    a: 'postAreaCenter / postAreaRadiusM と OMT_URL / PROTOMAPS_URL 環境変数の設定を確認してください。z16 以上は元データ (Protomaps planet) が z15 までしか持たないため使用不可。',
  },
  {
    q: 'セッション指定 URL でセッションのチェックが入らない',
    a: '参加者にブラウザをリロード (PWA の場合は再起動) してもらってください。古いキャッシュが残っている可能性があります。',
  },
];

const GLOSSARY: { term: string; def: string }[] = [
  { term: 'colorRule', def: 'マップ上のピン色を決めるルール (4 象限 / 単一軸の値判定式)' },
  { term: 'rootKey', def: 'AI 解析結果 JSON を包む最上位キー名 (例: ecw_w)' },
  {
    term: 'bucketMethod',
    def: 'マトリクス軸の値分類方式 (auto/each-value/mean-split/positive-negative/by-name/custom-ranges)',
  },
  { term: 'aggregator', def: '分類スコアの集約関数 (avg/sum/max/min/median)' },
  { term: 'postDetailScores', def: '投稿詳細に表示するスコア列の定義 (M5 analysisDisplayConfigs)' },
  { term: 'visible / displayOrder', def: '投稿詳細での項目表示 / 表示順 (data-settings)' },
  {
    term: 'aiStatus',
    def: 'AI 解析の状態 (pending / processing / done / failed / skipped / null)',
  },
  { term: 'urlToken', def: '地区アクセス用 32-byte hex token。`/m/<urlToken>` で参加者がアクセス' },
  { term: 'accessibleDistricts', def: 'Custom Claims に保持される参加地区 ID の配列 (最大 20)' },
];

export function AdminGuidePage() {
  return (
    <>
      <header className="mb-6">
        <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 hover:underline">
          ← ホーム
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">管理者ガイド</h1>
        <p className="text-sm text-slate-500 mt-1">
          admin app の主要機能を 10 領域で解説します。詳細は <code>docs/</code>{' '}
          配下を参照してください。
          <span className="ml-1 text-xs text-purple-700">★</span> は master 限定機能です。
        </p>
      </header>

      <TableOfContents />

      <div className="grid grid-cols-1 gap-4">
        {SECTIONS.map((s) => (
          <Card key={s.id} padding="lg" id={s.id} className="scroll-mt-4">
            <h2 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
              <span>{s.title}</span>
              {s.masterOnly && (
                <span className="inline-block bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
                  master 限定 ★
                </span>
              )}
            </h2>
            {s.body.map((p, i) => (
              <p key={i} className="text-sm text-slate-700 mb-2 last:mb-0">
                {p}
              </p>
            ))}
            {s.bullets && s.bullets.length > 0 && (
              <ul className="mt-2 list-disc list-inside text-sm text-slate-700 space-y-0.5">
                {s.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
            {s.tables && s.tables.map((tbl, ti) => <GuideTableView key={ti} table={tbl} />)}
            {s.hint && <Hint>{s.hint}</Hint>}
            {s.links && s.links.length > 0 && (
              <ul className="mt-3 grid grid-cols-1 gap-1">
                {s.links.map((l) => (
                  <li key={l.to}>
                    {l.external ? (
                      <a
                        href={l.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {l.label} ↗
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {l.label} →
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}

        {/* 用語集 */}
        <Card padding="lg" id="glossary" className="scroll-mt-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">📖 用語集</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {GLOSSARY.map((g) => (
              <div key={g.term}>
                <dt className="font-semibold text-slate-800">{g.term}</dt>
                <dd className="text-slate-600">{g.def}</dd>
              </div>
            ))}
          </dl>
        </Card>

        {/* トラブルシュート */}
        <Card padding="lg" id="troubleshoot" className="scroll-mt-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">🛠 トラブルシューティング</h2>
          <div className="grid grid-cols-1 gap-2">
            {TROUBLESHOOT.map((qa, i) => (
              <details key={i} className="border border-slate-200 rounded p-3">
                <summary className="font-medium text-slate-800 cursor-pointer text-sm">
                  Q: {qa.q}
                </summary>
                <p className="text-sm text-slate-700 mt-2">A: {qa.a}</p>
              </details>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

function TableOfContents() {
  return (
    <nav className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
      <h2 className="text-sm font-semibold text-slate-600 mb-2">目次</h2>
      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} className="text-sm text-primary-600 hover:underline">
              {s.title}
            </a>
          </li>
        ))}
        <li>
          <a href="#glossary" className="text-sm text-primary-600 hover:underline">
            📖 用語集
          </a>
        </li>
        <li>
          <a href="#troubleshoot" className="text-sm text-primary-600 hover:underline">
            🛠 トラブルシューティング
          </a>
        </li>
      </ul>
    </nav>
  );
}

/** AW10 (docs/108、old2 GuidePage ロール表 / ModeTable 踏襲): セクション内の表を描画。 */
function GuideTableView({ table }: { table: GuideTable }) {
  return (
    <div className="mt-3 overflow-x-auto">
      {table.caption && (
        <p className="text-xs font-semibold text-slate-600 mb-1">{table.caption}</p>
      )}
      <table className="text-sm border-collapse w-full">
        <thead>
          <tr className="bg-slate-100">
            {table.headers.map((h, i) => (
              <th
                key={i}
                className="border border-slate-300 p-2 text-left font-semibold text-slate-700"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-slate-300 p-2 text-slate-700 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return (
    <div className="mt-3 bg-info-50 border border-info-100 rounded p-2 text-xs text-info-700">
      <span className="font-semibold">ヒント: </span>
      {children}
    </div>
  );
}
